import { useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import { isEmpty, isEqual } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NoteInput } from 'sr-types/lib/note/v1/graphql';
import { Note } from 'sr-types/lib/search/v1/graphql';
import { COLOR_STATUS_NOTE, COLOR_STATUS_NOTE_DARK } from '../../../Colors';
import { useStyling } from '../../../Theme';
import { SECURITY_ACTIONS, useEntitySecurityPath, useSecurity } from '../../../auth/Security';
import { UserContext } from '../../../auth/UserContext';
import type { SecuredActionType } from '../../../components/ActionType';
import SidePanel from '../../../components/layout/SidePanel';
import { BOOLEAN_STRINGS_CONSTANTS, TEMP_ID_PREFIX } from '../../../constants/constants';
import { ENTITY_CONSTANTS } from '../../../constants/entityConstants';
import { useDeepLinkURL, useTrackDeepLinkItemId } from '../../../deepLink';
import { useI18n } from '../../../i18n/I18n';
import { Icons } from '../../../icons/Icons';
import { useShareSettings } from '../../../share';
import { failedToastMessage, generateTempId, successToastMessage } from '../../../utils/commonUtils';
import { client, removeNote, saveNote } from '../../data/notesGraphqlApi';
import { useGetNote } from '../../data/useGetNoteApi';
import { useSearchNotes } from '../../data/useSearchNotes';
import type { NotesProps } from '../../types';
import {
    getAvailableTagsFromNotes,
    getEmptyNote,
    getNoteOwner,
    makeStatusNoteIdFirst,
    noteMatchesFilter,
    setNoteAfterSave,
    stateToNoteInput,
    stripHtmlToText,
    syncStatusNoteIntoNotes
} from '../../utils';
import { useNotesContext } from '../hooks/useNotesContext';
import { useNotesSharePanel } from '../hooks/useNotesSharePanel';
import { usePendingNoteActions } from '../hooks/usePendingNoteActions';
import { NEW_NOTE_SHARE_SETTINGS_SCOPE_ID } from '../shareSettingsScope';
import NotesManager from './NotesManager';
import NoteShareSettings from './dataInput/NoteShareSettings';

const SORTING_ENABLED = true;
const EMPTY_SECURED_ACTIONS: SecuredActionType[] = [];

const notesSorter = (a: Note, b: Note, sortAsc: boolean) => {
    return sortAsc
        ? a.updatedOn < b.updatedOn
            ? -1
            : a.updatedOn > b.updatedOn
              ? 1
              : 0
        : a.updatedOn < b.updatedOn
          ? 1
          : a.updatedOn > b.updatedOn
            ? -1
            : 0;
};

export default function NotesContent({
    id,
    title = 'Notes',
    entityReference,
    onClose,
    renderWithoutSidePanel = false,
    hideSearchAndEditor = false,
    triggerRefetchData,
    onAssociateStatusNote,
    highlightedNoteId,
    singleNoteView,
    showNotes = true,
    customSidePanelActions = EMPTY_SECURED_ACTIONS,
    focusTrigger = 0
}: NotesProps) {
    const { isDarkMode } = useStyling();
    const { userProfile, activeOrganizationAccount } = useContext(UserContext);
    const {
        pendingShareSettingsForNewNote,
        setPendingShareSettingsForNewNote,
        newNoteDraft,
        setNewNoteDraft,
        editingNote,
        setEditingNote,
        isInEditMode,
        setIsInEditMode,
        setNotes: setNotesInContext,
        selectedNote
    } = useNotesContext();
    const deepLinkUIStatePath = useEntitySecurityPath();
    const { getDeepLinkURL } = useDeepLinkURL();
    const { sharePanelOpen, openShareForExistingNote, openShareForNewNote } = useNotesSharePanel();
    const { saveShareSettings, getShareSettings } = useShareSettings();
    const [loading, setLoading] = useState(null);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const shouldSkipNoteQuery =
        !entityReference?.id ||
        (onAssociateStatusNote?.isStatusNoteAction && !!singleNoteView) ||
        !!singleNoteView?.noteId;

    const { noteData, isNoteLoading, noteFetchError, noteRefetch } = useSearchNotes(
        entityReference?.id,
        shouldSkipNoteQuery
    );
    const { noteDetails, isNoteLoading: isSingleNoteLoading } = useGetNote(
        singleNoteView?.noteId,
        !singleNoteView?.noteId || !!singleNoteView?.noteData
    );
    const { addPendingNoteAction, triggerPendingNoteActions } = usePendingNoteActions();

    const highlightedNoteRef = useRef<HTMLDivElement>(null);
    const t = useI18n();
    const shouldHideSearchAndEditor =
        hideSearchAndEditor || (onAssociateStatusNote?.isStatusNoteAction && !!singleNoteView);
    const showOnlyEditor = !!onAssociateStatusNote?.isStatusNoteAction;

    const collapsedKey = `${id}-collapsed`;
    const sortKey = `${id}-sort`;

    const emptyNote = getEmptyNote(entityReference, userProfile);

    const [collapsed, setCollapsed] = useState(localStorage.getItem(collapsedKey) === BOOLEAN_STRINGS_CONSTANTS.true);
    const [sortAsc, setSortAsc] = useState(localStorage.getItem(sortKey) === BOOLEAN_STRINGS_CONSTANTS.true);
    const [term, setTerm] = useState<string>('');
    const [notes, setNotes] = useState<Note[]>([]);
    const [saveMutation] = useMutation(saveNote, { client: client });
    const [deleteMutation, { loading: isDeleting }] = useMutation(removeNote, { client: client });
    const [deleteNoteId, setDeleteNoteId] = useState(undefined);
    const [resetEditor, setResetEditor] = useState(false);

    const statusNoteId = useMemo(
        () => onAssociateStatusNote?.statusNote?.id ?? onAssociateStatusNote?.statusNote?.reference?.id,
        [onAssociateStatusNote?.statusNote?.id, onAssociateStatusNote?.statusNote?.reference?.id]
    );

    useTrackDeepLinkItemId(selectedNote?.id, deepLinkUIStatePath);

    useEffect(() => {
        setNotesInContext((prevNotesInContext) => {
            if (isEqual(prevNotesInContext, notes)) {
                return prevNotesInContext;
            }
            return notes;
        });
    }, [notes, setNotesInContext]);

    const singleNoteSecurity = singleNoteView?.noteData?.security ?? noteDetails?.note?.security;
    const { allowed: canEditNoteBySecurity } = useSecurity(SECURITY_ACTIONS.EDIT, {
        entityName: ENTITY_CONSTANTS.note,
        entitySecurity: singleNoteSecurity
    });
    const canEditSingleNote = Boolean(singleNoteView && !singleNoteView.disableEditing && canEditNoteBySecurity);

    useEffect(() => {
        if (resetEditor) {
            setResetEditor(false);
        }
    }, [resetEditor]);

    useEffect(() => {
        if (singleNoteView && canEditSingleNote) {
            setIsInEditMode(!singleNoteView?.disableEditing);
        } else if (notes) {
            setIsInEditMode(false);
        }
    }, [singleNoteView, notes, setIsInEditMode, canEditSingleNote]);

    const securedActions = useMemo<SecuredActionType[]>(() => {
        const actions: SecuredActionType[] = [
            ...customSidePanelActions,
            {
                icon: collapsed ? Icons.UnfoldMore : Icons.UnfoldLess,
                label: t(collapsed ? 'dialog.expand' : 'dialog.collapse'),
                onClick: () =>
                    setCollapsed(() => {
                        localStorage.setItem(collapsedKey, (!collapsed).toString());
                        return !collapsed;
                    })
            },
            {
                icon: Icons.Close,
                label: t('dialog.close'),
                onClick: onClose
            }
        ];

        if (SORTING_ENABLED && !singleNoteView) {
            actions.unshift({
                icon: sortAsc ? Icons.ArrowUpward : Icons.ArrowDownward,
                label: t(sortAsc ? 'dialog.sort.descending' : 'dialog.sort.ascending'),
                onClick: () =>
                    setSortAsc((prevSortAsc) => {
                        localStorage.setItem(sortKey, (!prevSortAsc).toString());
                        return !prevSortAsc;
                    })
            });
        }

        return actions;
    }, [collapsed, collapsedKey, customSidePanelActions, onClose, singleNoteView, sortAsc, sortKey, t]);

    useEffect(() => {
        setNotes((prevNotes) => {
            if (prevNotes?.length === 0) {
                return prevNotes;
            }

            const sorted = [...prevNotes].sort((a, b) => notesSorter(a, b, sortAsc));
            return statusNoteId ? makeStatusNoteIdFirst(statusNoteId, sorted) : sorted;
        });
    }, [sortAsc, notes?.length, statusNoteId]);

    useEffect(() => {
        setLoading(isNoteLoading || isSingleNoteLoading);
    }, [isNoteLoading, isSingleNoteLoading]);

    const availableTags = useMemo(() => getAvailableTagsFromNotes(notes), [notes]);

    const filteredNotes: Note[] = useMemo(
        () => notes.filter((n) => noteMatchesFilter(n, term, selectedTags)),
        [notes, term, selectedTags]
    );

    const getNoteSx = (noteId: string) => {
        const isStatus = noteId === statusNoteId;
        const isSingleView = Boolean(singleNoteView?.noteData || noteDetails?.note?.reference?.id);

        return {
            ...(isStatus && {
                border: `1px solid ${isDarkMode ? COLOR_STATUS_NOTE_DARK : COLOR_STATUS_NOTE}`
            }),
            ...(isSingleView && {
                maxHeight: '100%'
            })
        };
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (highlightedNoteRef.current && highlightedNoteId) {
                highlightedNoteRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
        return () => clearTimeout(timeout);
    }, [highlightedNoteId, filteredNotes]);

    useEffect(() => {
        setNotes((prevNotes) =>
            syncStatusNoteIntoNotes(
                prevNotes,
                onAssociateStatusNote?.statusNote,
                onAssociateStatusNote?.deletedStatusNoteId,
                userProfile?.name
            )
        );
    }, [
        onAssociateStatusNote?.statusNote,
        onAssociateStatusNote?.deletedStatusNoteId,
        notes?.length,
        userProfile?.name
    ]);

    useEffect(() => {
        if (noteFetchError) {
            console.error('Failed to fetch notes:', noteFetchError);
            return;
        }
        if (singleNoteView?.noteData || noteDetails?.note) {
            const note = singleNoteView?.noteData || { ...noteDetails?.note, id: noteDetails?.note?.reference?.id };
            setNotes([note as unknown as Note]);
            if (canEditSingleNote) {
                setEditingNote(note as unknown as Note);
            }
            return;
        }
        if (!singleNoteView && noteData?.results?.hits?.items) {
            const incomingNotes = [...noteData.results.hits.items];
            if (SORTING_ENABLED) {
                incomingNotes.sort((a, b) => notesSorter(a, b, sortAsc));
            }
            setNotes(incomingNotes);
            setNotesInContext(incomingNotes);
        }
    }, [
        canEditSingleNote,
        noteData,
        noteDetails?.note,
        noteFetchError,
        setEditingNote,
        setNotesInContext,
        singleNoteView,
        sortAsc
    ]);

    const onCancelNewNote = useCallback(() => {
        setNewNoteDraft(null);
        setResetEditor((prev) => !prev);
    }, [setNewNoteDraft]);

    const onCancelExistingNote = useCallback(() => {
        setEditingNote(null);
        setIsInEditMode(false);
    }, [setEditingNote, setIsInEditMode]);

    const doSave = useCallback(
        async (modified: NoteInput, original: Note, isNewNote: boolean) => {
            const originalNoteCopy: Note = { ...original };
            const tempId = isNewNote ? `${TEMP_ID_PREFIX}${generateTempId().toString()}` : original.id;
            const noteOwner = getNoteOwner(originalNoteCopy.updatedByName, userProfile?.name);

            const optimisticNote: Note = {
                ...originalNoteCopy,
                ...modified,
                updatedByName: noteOwner,
                id: tempId,
                reference: { id: tempId }
            };

            const sortNotesIfEnabled = (notes: Note[]) => {
                if (SORTING_ENABLED) {
                    return [...notes].sort((a, b) => notesSorter(a, b, sortAsc));
                }
                return notes;
            };

            if (modified.identity.id === statusNoteId) {
                onAssociateStatusNote?.onStatusNoteChanged?.(
                    { ...originalNoteCopy, ...modified, updatedByName: noteOwner },
                    entityReference?.id
                );
            }

            const isCreatingNewStatusNote = isNewNote && onAssociateStatusNote?.isStatusNoteAction;
            if (isCreatingNewStatusNote && typeof onAssociateStatusNote?.onStatusNoteChanged === 'function') {
                onAssociateStatusNote.onStatusNoteChanged(optimisticNote, entityReference?.id);
            }

            setNotes((prev) => {
                const exists = prev.some((n) => n.id === optimisticNote.id);
                const updated = exists
                    ? prev.map((n) => (n.id === optimisticNote.id ? optimisticNote : n))
                    : [optimisticNote, ...prev];
                const idToMakeFirst = isCreatingNewStatusNote ? optimisticNote.id : statusNoteId;
                const sorted = sortNotesIfEnabled(updated);
                return idToMakeFirst ? makeStatusNoteIdFirst(idToMakeFirst, sorted) : sorted;
            });

            isNewNote ? onCancelNewNote() : onCancelExistingNote();

            if (onAssociateStatusNote?.isStatusNoteAction && !isNewNote) {
                onClose();
            }

            const rollback = () => {
                setNotes((prev) =>
                    isNewNote
                        ? prev.filter((n) => n.id !== tempId)
                        : prev.map((n) => (n.id === tempId || n.id === originalNoteCopy.id ? originalNoteCopy : n))
                );
                if (onAssociateStatusNote?.isStatusNoteAction) {
                    onAssociateStatusNote?.onStatusNoteChanged?.(null, entityReference?.id);
                }

                if (modified.identity.id === statusNoteId) {
                    onAssociateStatusNote?.onStatusNoteChanged?.({ ...originalNoteCopy }, entityReference?.id);
                }
            };

            try {
                const newNoteShareSettings = getShareSettings(NEW_NOTE_SHARE_SETTINGS_SCOPE_ID);
                const input = {
                    ...modified,
                    contentType: modified.contentType || 'text/html'
                };
                if (isNewNote && newNoteShareSettings) {
                    input.restricted = newNoteShareSettings.accessLevel === 'restricted';
                }
                const res = await saveMutation({
                    variables: {
                        input
                    },
                    context: {
                        headers: {
                            ownerId: activeOrganizationAccount
                        }
                    }
                });

                if (res.data.saveNote.errors) {
                    failedToastMessage('notes.toast.save.failure');
                    rollback();
                    return;
                }

                const newNote: Note = setNoteAfterSave(
                    modified,
                    { ...originalNoteCopy, updatedByName: noteOwner },
                    res
                );

                triggerPendingNoteActions(tempId, newNote.id);

                let didSaveShareSettingsFail = false;
                if (pendingShareSettingsForNewNote) {
                    try {
                        const deepLinkURL = getDeepLinkURL(deepLinkUIStatePath, { id: newNote.id });
                        await saveShareSettings(NEW_NOTE_SHARE_SETTINGS_SCOPE_ID, { id: newNote.id }, deepLinkURL);
                    } catch {
                        didSaveShareSettingsFail = true;
                    } finally {
                        setPendingShareSettingsForNewNote(null);
                    }
                }

                successToastMessage('notes.toast.save.success');

                if (didSaveShareSettingsFail) {
                    failedToastMessage('notes.share.settings.saved.failure');
                }

                setNotes((prev) => {
                    const updated = prev.map((n) => (n.id === tempId || n.id === newNote.id ? newNote : n));
                    const idToMakeFirst = isCreatingNewStatusNote ? newNote.id : statusNoteId;
                    const sorted = sortNotesIfEnabled(updated);
                    return idToMakeFirst ? makeStatusNoteIdFirst(idToMakeFirst, sorted) : sorted;
                });
                if (onAssociateStatusNote?.isStatusNoteAction) {
                    onAssociateStatusNote?.saveFunction?.(newNote);
                    if (isNewNote) {
                        onClose();
                    }
                }

                triggerRefetchData?.();
            } catch {
                failedToastMessage('notes.toast.save.failure');
                rollback();
            }
        },
        [
            userProfile?.name,
            statusNoteId,
            onAssociateStatusNote,
            onCancelNewNote,
            onCancelExistingNote,
            sortAsc,
            entityReference?.id,
            onClose,
            saveMutation,
            activeOrganizationAccount,
            triggerPendingNoteActions,
            pendingShareSettingsForNewNote,
            triggerRefetchData,
            getDeepLinkURL,
            deepLinkUIStatePath,
            saveShareSettings,
            getShareSettings,
            setPendingShareSettingsForNewNote
        ]
    );

    const doDelete = useCallback(
        async (noteId: string) => {
            try {
                const res = await deleteMutation({
                    variables: {
                        id: noteId
                    },
                    context: {
                        headers: {
                            ownerId: activeOrganizationAccount
                        }
                    }
                });

                if (res.data.deleteNote.errors) {
                    console.error('Delete note error:', res.data.deleteNote.errors);
                } else {
                    if (noteId === statusNoteId) {
                        onAssociateStatusNote?.onStatusNoteDeleted?.();
                        onAssociateStatusNote?.onStatusNoteChanged?.(null, entityReference?.id);
                    }
                    setNotes((prevNotes) => prevNotes.filter((note) => note.id !== noteId));
                }
                setDeleteNoteId(undefined);
                triggerRefetchData?.();
            } catch (err) {
                console.warn(err);
            }
        },
        [
            deleteMutation,
            activeOrganizationAccount,
            triggerRefetchData,
            statusNoteId,
            onAssociateStatusNote,
            entityReference?.id
        ]
    );

    const onDeleteHandle = (noteId: string) => {
        return new Promise((resolve, reject) => {
            setDeleteNoteId(noteId);
        });
    };

    const onConfirmDelete = () => {
        if (deleteNoteId.startsWith(TEMP_ID_PREFIX)) {
            addPendingNoteAction?.(deleteNoteId, (noteId) => doDelete(noteId));
        } else {
            doDelete(deleteNoteId);
        }
    };
    const onTagChangeHandlerForNewNote = (name: string, value: string[]) =>
        setNewNoteDraft((prev) => ({ ...prev, [name]: value }));

    const onTagChangeHandlerForExistingNote = (name: string, value: string[], note: Note) =>
        setEditingNote((prev) => (prev && prev.id === note.id ? { ...prev, [name]: value } : prev));

    const disableSaveForNewNote = !isEmpty(editingNote) || isEmpty(stripHtmlToText(newNoteDraft?.content || ''));

    const notesContent = (
        <NotesManager
            noteFetchError={noteFetchError}
            hideSearchAndEditor={shouldHideSearchAndEditor}
            showOnlyEditor={showOnlyEditor}
            term={term}
            setTerm={setTerm}
            availableTags={availableTags}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            t={t}
            emptyNoteContent={emptyNote.content}
            resetEditor={resetEditor}
            onNewNoteEditorChange={(content, title) => setNewNoteDraft((prev) => ({ ...prev, content, title }))}
            newNoteDraft={newNoteDraft}
            onTagChangeHandlerForNewNote={onTagChangeHandlerForNewNote}
            onSaveNewNote={() =>
                doSave(
                    stateToNoteInput(newNoteDraft, entityReference),
                    getEmptyNote(entityReference, userProfile),
                    true
                )
            }
            onCancelNewNote={onCancelNewNote}
            disableSaveForNewNote={disableSaveForNewNote}
            onNewNoteShareClick={openShareForNewNote}
            onExistingNoteShareClick={openShareForExistingNote}
            showNotes={showNotes}
            loading={loading}
            filteredNotes={filteredNotes}
            highlightedNoteId={highlightedNoteId}
            highlightedNoteRef={highlightedNoteRef}
            editingNote={editingNote}
            collapsed={collapsed}
            setEditingNote={setEditingNote}
            isInEditMode={isInEditMode}
            setIsInEditMode={setIsInEditMode}
            onExistingNoteChange={(note, content, title) =>
                setEditingNote((prev) => (prev && prev.id === note.id ? { ...prev, content, title } : prev))
            }
            onTagChangeHandlerForExistingNote={onTagChangeHandlerForExistingNote}
            onSaveExistingNote={(note) => doSave(stateToNoteInput(editingNote, entityReference), note, false)}
            onCancelExistingNote={onCancelExistingNote}
            onDeleteHandle={onDeleteHandle}
            onAssociateStatusNote={onAssociateStatusNote}
            getNoteSx={getNoteSx}
            addPendingNoteAction={addPendingNoteAction}
            deleteNoteId={deleteNoteId}
            onConfirmDelete={onConfirmDelete}
            onCancelDelete={() => setDeleteNoteId(undefined)}
            isDeleting={isDeleting}
            focusTrigger={focusTrigger}
        />
    );

    const notesOrShareSettingsContent = (
        <Box sx={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}>
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>{notesContent}</Box>
            {sharePanelOpen && (
                <Box sx={{ width: '100%' }}>
                    <NoteShareSettings
                        onAfterExistingShareSettingsSave={shouldSkipNoteQuery ? undefined : noteRefetch}
                        deepLinkUIStatePath={deepLinkUIStatePath}
                    />
                </Box>
            )}
        </Box>
    );

    if (renderWithoutSidePanel) return notesOrShareSettingsContent;

    return (
        <SidePanel
            id={'notes-container'}
            dataTestId={'shareSettingsPanel'}
            title={title}
            securedActions={securedActions}
        >
            {notesOrShareSettingsContent}
        </SidePanel>
    );
}
